import { dayFromToday, isCustomFieldValueBlank, localDayOf } from "@salestrak/custom-fields";

import { INLINE_FIELD_ID_PREFIX, isFieldItem, resolveItemField } from "./items.ts";
import { stringIds } from "./submission.ts";
import type { FormCondition, FormConditionOp, FormField, FormFieldItem, FormItem } from "./types.ts";

export function conditionOpsFor(field: FormField | null): FormConditionOp[] {
  if (!field) return [];
  if (field.type === "select") return ["is", "isNot", "isAnswered", "isBlank"];
  if (field.type === "labels") return ["includes", "excludes", "isAnswered", "isBlank"];
  if (field.type === "checkbox") return ["isChecked", "isNotChecked"];
  if (field.type === "number" || field.type === "money") {
    return ["isAnswered", "isBlank", "greaterThan", "lessThan", "between"];
  }
  if (field.type === "date" || field.type === "dateTime") {
    return ["isAnswered", "isBlank", "before", "after", "withinLastDays"];
  }
  if (field.type === "photos") return ["isAnswered", "isBlank", "atLeast", "fewerThan"];
  return ["isAnswered", "isBlank"];
}

const COMPARISON_OPS: ReadonlySet<FormConditionOp> = new Set([
  "greaterThan",
  "lessThan",
  "between",
  "before",
  "after",
  "withinLastDays",
  "atLeast",
  "fewerThan",
]);

export function isComparisonOp(op: FormConditionOp): boolean {
  return COMPARISON_OPS.has(op);
}

export function comparisonArity(op: FormConditionOp): number {
  return op === "between" ? 2 : 1;
}

export function countFloor(op: FormConditionOp): number | null {
  if (op === "atLeast" || op === "fewerThan") return 1;
  return op === "withinLastDays" ? 0 : null;
}

export function conditionNeedsValues(op: FormConditionOp): boolean {
  return op === "is" || op === "isNot" || op === "includes" || op === "excludes";
}

export function isComparisonComplete(condition: FormCondition): boolean {
  const values = condition.values ?? [];
  const wanted = comparisonArity(condition.op);
  if (values.length < wanted) return false;
  return values
    .slice(0, wanted)
    .every((value) =>
      condition.op === "before" || condition.op === "after"
        ? localDayOf(value) !== null
        : countFloor(condition.op) !== null
          ? /^\d+$/.test(value.trim()) && Number(value) >= (countFloor(condition.op) ?? 0)
          : value.trim() !== "" && Number.isFinite(Number(value)),
    );
}

export function answerOptionValues(value: unknown): string[] {
  if (Array.isArray(value)) return stringIds(value);
  return typeof value === "string" && value !== "" ? [value] : [];
}

export function ruleValues(condition: FormCondition, field: FormField): string[] {
  const values = condition.values ?? [];
  if (!field.customFieldId.startsWith(INLINE_FIELD_ID_PREFIX)) return values;
  const offered = new Set((field.config?.options ?? []).map((option) => option?.value ?? option?.label ?? ""));
  return values.filter((value) => offered.has(value));
}

function comparisonMet(condition: FormCondition, value: unknown, timeZone?: string): boolean {
  const [first = "", second = ""] = condition.values ?? [];
  switch (condition.op) {
    case "greaterThan":
    case "lessThan":
    case "between": {
      const amount = typeof value === "number" ? value : Number(value);
      if (!Number.isFinite(amount)) return false;
      if (condition.op === "greaterThan") return amount > Number(first);
      if (condition.op === "lessThan") return amount < Number(first);
      const [low = 0, high = 0] = [Number(first), Number(second)].sort((a, b) => a - b);
      return amount >= low && amount <= high;
    }
    case "before":
    case "after":
    case "withinLastDays": {
      const day = localDayOf(value, timeZone);
      if (!day) return false;
      if (condition.op === "before" || condition.op === "after") {
        const bound = localDayOf(first, timeZone);
        if (!bound) return false;
        return condition.op === "before" ? day < bound : day > bound;
      }
      return day >= dayFromToday(-Number(first), timeZone) && day <= dayFromToday(0, timeZone);
    }
    case "atLeast":
      return answerOptionValues(value).length >= Number(first);
    case "fewerThan":
      return answerOptionValues(value).length < Number(first);
    default:
      return true;
  }
}

/** An unanswerable or unfinished rule reads true, so a question never vanishes for an invisible reason. */
export function conditionMet(condition: FormCondition, field: FormField, value: unknown, timeZone?: string): boolean {
  if (!conditionOpsFor(field).includes(condition.op)) return true;
  const blank = isCustomFieldValueBlank(value);
  if (isComparisonOp(condition.op)) {
    if (!isComparisonComplete(condition)) return true;
    if (blank) return condition.op === "fewerThan";
    return comparisonMet(condition, value, timeZone);
  }
  switch (condition.op) {
    case "isAnswered":
      return !blank;
    case "isBlank":
      return blank;
    case "isChecked":
      return value === true;
    case "isNotChecked":
      return value !== true;
    default: {
      const wanted = ruleValues(condition, field);
      if (wanted.length === 0) return true;
      const hit = answerOptionValues(value).some((entry) => wanted.includes(entry));
      return condition.op === "is" || condition.op === "includes" ? hit : !hit;
    }
  }
}

export function hiddenItemIds<F extends FormField>(
  items: readonly FormItem<F>[],
  answers: Record<string, unknown>,
  catalog: ReadonlyMap<string, F>,
  timeZone?: string,
): Set<string> {
  const hidden = new Set<string>();
  const seen = new Map<string, FormItem<F>>();
  for (const item of items) {
    const condition = item.kind === "section" ? undefined : item.showWhen;
    seen.set(item.itemId, item);
    if (!condition) continue;
    const trigger = seen.get(condition.whenItemId);
    if (!trigger || trigger === item || !isFieldItem(trigger)) continue;
    if (hidden.has(trigger.itemId)) {
      hidden.add(item.itemId);
      continue;
    }
    const field = resolveItemField(trigger, catalog);
    if (field && !conditionMet(condition, field, answers[trigger.itemId], timeZone)) hidden.add(item.itemId);
  }
  return hidden;
}

export function conditionSources<F extends FormField>(items: readonly FormItem<F>[], itemId: string): FormFieldItem<F>[] {
  const index = items.findIndex((item) => item.itemId === itemId);
  if (index < 0) return [];
  return items.slice(0, index).filter((item): item is FormFieldItem<F> => isFieldItem(item));
}
